import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Sparkles, Wand2 } from 'lucide-react';

interface ReparseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billId: string;
  currentData?: any;
}

export function ReparseDialog({ open, onOpenChange, billId, currentData }: ReparseDialogProps) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [modelStrategy, setModelStrategy] = useState<'primary' | 'fallback' | 'all' | 'custom'>('primary');
  const [customModel, setCustomModel] = useState('gpt-4o-mini');
  
  const [imageEnhance, setImageEnhance] = useState(true);
  const [autoDeskew, setAutoDeskew] = useState(true);
  const [removeBackground, setRemoveBackground] = useState(false);
  const [increaseContrast, setIncreaseContrast] = useState(true);
  
  const [ocrEngine, setOcrEngine] = useState('google');
  const [languages, setLanguages] = useState(['en']);
  
  const [forceReextraction, setForceReextraction] = useState(true);
  const [highTokens, setHighTokens] = useState(false);
  const [debugLogging, setDebugLogging] = useState(false);

  const handleReparse = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('multi-tier-bill-parser', {
        body: {
          bill_id: billId,
          options: {
            modelStrategy,
            customModel: modelStrategy === 'custom' ? customModel : undefined,
            imageProcessing: {
              enhance: imageEnhance,
              deskew: autoDeskew,
              removeBackground,
              contrast: increaseContrast,
            },
            ocr: {
              engine: ocrEngine,
              languages,
            },
            forceReextraction,
            maxTokens: highTokens ? 8000 : 4000,
            debug: debugLogging,
          },
        },
      });

      if (error) throw error;

      toast({
        title: '✨ Re-parsing started',
        description: 'The bill is being re-processed with your selected options.',
      });

      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Re-parse failed',
        description: error.message || 'Failed to start re-parsing',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleLanguage = (lang: string) => {
    setLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Re-parse Bill
          </DialogTitle>
          <DialogDescription>
            Configure re-parsing options to improve extraction accuracy
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Model Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">AI Model Strategy</Label>
            <RadioGroup value={modelStrategy} onValueChange={(v: any) => setModelStrategy(v)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="primary" id="primary" />
                <Label htmlFor="primary" className="font-normal cursor-pointer">
                  Primary Parser (Fast)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fallback" id="fallback" />
                <Label htmlFor="fallback" className="font-normal cursor-pointer">
                  Fallback Parser (More Accurate)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="font-normal cursor-pointer">
                  Try All Models (Best Effort)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="custom" id="custom" />
                <Label htmlFor="custom" className="font-normal cursor-pointer">
                  Custom Model
                </Label>
              </div>
            </RadioGroup>

            {modelStrategy === 'custom' && (
              <Select value={customModel} onValueChange={setCustomModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o (Most Accurate)</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Balanced)</SelectItem>
                  <SelectItem value="mistralai/mistral-large-latest">Mistral Large</SelectItem>
                  <SelectItem value="deepseek/deepseek-chat">DeepSeek Chat</SelectItem>
                  <SelectItem value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Image Enhancement */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Image Enhancement (for scanned bills)</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enhance"
                  checked={imageEnhance}
                  onCheckedChange={(c) => setImageEnhance(!!c)}
                />
                <Label htmlFor="enhance" className="font-normal cursor-pointer">
                  Auto-enhance image quality
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deskew"
                  checked={autoDeskew}
                  onCheckedChange={(c) => setAutoDeskew(!!c)}
                />
                <Label htmlFor="deskew" className="font-normal cursor-pointer">
                  Auto-deskew rotated images
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="background"
                  checked={removeBackground}
                  onCheckedChange={(c) => setRemoveBackground(!!c)}
                />
                <Label htmlFor="background" className="font-normal cursor-pointer">
                  Remove backgrounds and noise
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="contrast"
                  checked={increaseContrast}
                  onCheckedChange={(c) => setIncreaseContrast(!!c)}
                />
                <Label htmlFor="contrast" className="font-normal cursor-pointer">
                  Increase contrast for better OCR
                </Label>
              </div>
            </div>
          </div>

          {/* OCR Settings */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">OCR Settings</Label>
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-normal">OCR Engine</Label>
                <Select value={ocrEngine} onValueChange={setOcrEngine}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google Cloud Vision (Best)</SelectItem>
                    <SelectItem value="tesseract">Tesseract v5 (Fast)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-normal mb-2 block">Languages</Label>
                <div className="flex flex-wrap gap-2">
                  {['en', 'es', 'fr', 'de'].map(lang => (
                    <div key={lang} className="flex items-center space-x-2">
                      <Checkbox
                        id={`lang-${lang}`}
                        checked={languages.includes(lang)}
                        onCheckedChange={() => toggleLanguage(lang)}
                      />
                      <Label htmlFor={`lang-${lang}`} className="font-normal cursor-pointer text-sm">
                        {lang === 'en' ? 'English' : lang === 'es' ? 'Spanish' : lang === 'fr' ? 'French' : 'German'}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Options */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Advanced Options</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="force"
                  checked={forceReextraction}
                  onCheckedChange={(c) => setForceReextraction(!!c)}
                />
                <Label htmlFor="force" className="font-normal cursor-pointer">
                  Force re-extraction (ignore cached results)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tokens"
                  checked={highTokens}
                  onCheckedChange={(c) => setHighTokens(!!c)}
                />
                <Label htmlFor="tokens" className="font-normal cursor-pointer">
                  Use higher max tokens (8000) for complex bills
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="debug"
                  checked={debugLogging}
                  onCheckedChange={(c) => setDebugLogging(!!c)}
                />
                <Label htmlFor="debug" className="font-normal cursor-pointer">
                  Enable debug logging for troubleshooting
                </Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button onClick={handleReparse} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Re-parsing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Start Re-parse
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
